import { useState, useEffect, useCallback, useRef } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { logsApi } from '../services/api';

const MIN_SEARCH_LENGTH = 3;
const MAX_SEARCH_LENGTH = 100;
const SEARCH_DEBOUNCE_MS = 400;

function normalizeSearch(value) {
  const normalized = String(value || '').trim().slice(0, MAX_SEARCH_LENGTH);
  return normalized.length >= MIN_SEARCH_LENGTH ? normalized : '';
}

export function useLogs(serviceName) {
  const [logs, setLogs] = useState([]);
  const [errors, setErrors] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [errorsLoading, setErrorsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [errorPagination, setErrorPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentErrorPage, setCurrentErrorPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const { isTenantContextReady, currentWorkspace } = useWorkspace();
  const [enabled, setEnabled] = useState(true);
  const logsRequestRef = useRef({ id: 0, controller: null });
  const errorsRequestRef = useRef({ id: 0, controller: null });

  const fetchLogs = useCallback(async (searchOverride = searchTerm, pageOverride = currentPage) => {
    const requestId = logsRequestRef.current.id + 1;
    logsRequestRef.current.controller?.abort();
    const controller = new AbortController();
    logsRequestRef.current = { id: requestId, controller };
    try {
      setLogsLoading(true);
      setError(null);

      const data = await logsApi.getLogs(
        serviceName,
        pageOverride,
        pageSize,
        searchOverride,
        controller.signal
      );

      if (logsRequestRef.current.id === requestId) {
        setLogs(data.logs || []);
        setPagination(data.pagination || null);
      }
    } catch (err) {
      if (err.name !== 'AbortError' && logsRequestRef.current.id === requestId) {
        setError(err.message);
      }
    } finally {
      if (logsRequestRef.current.id === requestId) {
        setLogsLoading(false);
      }
    }
  }, [serviceName, currentPage, pageSize, searchTerm]);

  const fetchErrorLogs = useCallback(async (
    searchOverride = searchTerm,
    pageOverride = currentErrorPage
  ) => {
    const requestId = errorsRequestRef.current.id + 1;
    errorsRequestRef.current.controller?.abort();
    const controller = new AbortController();
    errorsRequestRef.current = { id: requestId, controller };
    try {
      setErrorsLoading(true);
      const data = await logsApi.getErrorLogs(
        serviceName,
        pageOverride,
        pageSize,
        searchOverride,
        controller.signal
      );
      if (errorsRequestRef.current.id === requestId) {
        setErrors(data.logs || []);
        setErrorPagination(data.pagination || null);
      }
    } catch (err) {
      if (err.name !== 'AbortError' && errorsRequestRef.current.id === requestId) {
        setError(err.message);
      }
    } finally {
      if (errorsRequestRef.current.id === requestId) {
        setErrorsLoading(false);
      }
    }
  }, [serviceName, currentErrorPage, pageSize, searchTerm]);

  const fetchRecentLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      setError(null);
      
      const data = await logsApi.getRecentLogs(serviceName, currentPage, pageSize, searchTerm);
      setLogs(data.logs || []);
      setPagination(data.pagination || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLogsLoading(false);
    }
  }, [serviceName, currentPage, pageSize, searchTerm]);

  // Fetch each log collection independently so page changes do not duplicate requests.
  useEffect(() => {
    if (!isTenantContextReady || !currentWorkspace?.id) {
      logsRequestRef.current.controller?.abort();
      setLogs([]);
      setPagination(null);
      return;
    }
    fetchLogs();
  }, [fetchLogs, isTenantContextReady, currentWorkspace?.id]);

  useEffect(() => {
    if (!isTenantContextReady || !currentWorkspace?.id) {
      errorsRequestRef.current.controller?.abort();
      setErrors([]);
      setErrorPagination(null);
      return;
    }
    fetchErrorLogs();
  }, [fetchErrorLogs, isTenantContextReady, currentWorkspace?.id]);

  useEffect(() => () => {
    logsRequestRef.current.controller?.abort();
    errorsRequestRef.current.controller?.abort();
  }, []);

  useEffect(() => {
    setSearchValue('');
    setSearchTerm('');
    setCurrentPage(1);
    setCurrentErrorPage(1);
  }, [serviceName, currentWorkspace?.id]);

  const applySearch = useCallback((value) => {
    const nextSearch = normalizeSearch(value);
    if (nextSearch !== searchTerm) {
      setCurrentPage(1);
      setCurrentErrorPage(1);
      setSearchTerm(nextSearch);
    }
    return nextSearch;
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      applySearch(searchValue);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchValue, applySearch]);

  const handleSearchChange = useCallback((value) => {
    setSearchValue(String(value || '').slice(0, MAX_SEARCH_LENGTH));
  }, []);

  const submitSearch = useCallback(() => {
    const trimmedSearch = String(searchValue || '').trim().slice(0, MAX_SEARCH_LENGTH);
    if (trimmedSearch.length > 0 && trimmedSearch.length < MIN_SEARCH_LENGTH) {
      return;
    }
    const nextSearch = normalizeSearch(searchValue);
    setCurrentPage(1);
    setCurrentErrorPage(1);
    if (nextSearch === searchTerm) {
      fetchLogs(nextSearch, 1);
      fetchErrorLogs(nextSearch, 1);
    } else {
      setSearchTerm(nextSearch);
    }
  }, [searchValue, searchTerm, fetchLogs, fetchErrorLogs]);

  const clearSearch = useCallback(() => {
    setSearchValue('');
    setCurrentPage(1);
    setCurrentErrorPage(1);
    if (searchTerm) {
      setSearchTerm('');
    }
  }, [searchTerm]);

  const clearLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      setError(null);
      
      await logsApi.clearLogs(serviceName);
      setLogs([]);
      setErrors([]);
      setPagination(null);
      setErrorPagination(null);
      setCurrentPage(1);
      setCurrentErrorPage(1);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLogsLoading(false);
    }
  }, [serviceName]);

  const refreshLogs = useCallback(() => {
    fetchLogs(searchTerm);
    fetchErrorLogs(searchTerm);
  }, [fetchLogs, fetchErrorLogs, searchTerm]);

  // Listen to enabled toggle events to clear/stop UI when disabled
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.service === serviceName) {
        setEnabled(e.detail.enabled !== false);
      }
    };
    window.addEventListener('flow-enabled-changed', handler);
    return () => window.removeEventListener('flow-enabled-changed', handler);
  }, [serviceName]);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleErrorPageChange = useCallback((newPage) => {
    setCurrentErrorPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
    setCurrentErrorPage(1); // Reset error page too
  }, []);

  return {
    logs,
    errors,
    isLoading: logsLoading || errorsLoading,
    error,
    pagination,
    errorPagination,
    currentPage,
    currentErrorPage,
    pageSize,
    fetchLogs,
    fetchErrorLogs,
    fetchRecentLogs,
    clearLogs,
    refreshLogs,
    handlePageChange,
    handleErrorPageChange,
    handlePageSizeChange,
    searchValue,
    searchTerm,
    isSearchActive: searchTerm.length >= MIN_SEARCH_LENGTH,
    handleSearchChange,
    submitSearch,
    clearSearch,
  };
}
